import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { PortalLayout } from "@/components/PortalLayout";
import { SkeletonCards } from "@/components/States";
import { useSession } from "@/lib/session";
import { AcceptInvitation } from "@/pages/auth/AcceptInvitation";
import { ChangePassword } from "@/pages/auth/ChangePassword";
import { SignIn } from "@/pages/auth/SignIn";
import { SignInComplete } from "@/pages/auth/SignInComplete";
import { SignUp } from "@/pages/auth/SignUp";
import { BookingDetail } from "@/pages/portal/BookingDetail";
import { MyBookings } from "@/pages/portal/MyBookings";
import { QrRoom } from "@/pages/portal/QrRoom";
import { RoomSearch } from "@/pages/portal/RoomSearch";

// The admin portal is a separate chunk: an employee booking a room should
// never download the operational interface.
const AdminApp = lazy(() =>
  import("@/pages/admin/AdminApp").then((module) => ({ default: module.AdminApp })),
);

function FullPageLoading() {
  return (
    <div className="page">
      <SkeletonCards count={6} />
    </div>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useSession();
  if (loading) return <FullPageLoading />;
  if (!user) return <Navigate to="/sign-in" replace />;
  // Somebody else chose this password. The API refuses everything but changing
  // it, so there is nothing else worth rendering.
  if (user.must_change_password) return <ChangePassword forced />;
  return <>{children}</>;
}

export function App() {
  const { loading, user, canAccessAdmin } = useSession();

  return (
    <Routes>
      {/* This fires the instant sign-in sets a user, which is before the
          sign-in screen's own redirect can run — so an unconditional "/" here
          quietly overrode it and sent administrators to room search. */}
      <Route
        path="/sign-in"
        element={
          loading ? <FullPageLoading />
            : user ? <Navigate to={canAccessAdmin ? "/admin" : "/"} replace />
              : <SignIn />
        }
      />
      <Route path="/sign-in/complete" element={<SignInComplete />} />
      <Route path="/sign-up" element={<SignUp />} />
      {/* Reachable without a session — the whole point is that they do
          not have one yet. */}
      <Route path="/accept-invitation" element={<AcceptInvitation />} />
      {/* The QR route is reachable without a session: it shows the room and
          asks the visitor to sign in only when they want to book. */}
      <Route path="/r/:token" element={<QrRoom />} />

      <Route
        element={
          <RequireAuth>
            <PortalLayout />
          </RequireAuth>
        }
      >
        <Route path="/" element={<RoomSearch />} />
        <Route path="/bookings" element={<MyBookings />} />
        <Route path="/bookings/:bookingId" element={<BookingDetail />} />
        <Route path="/password" element={<ChangePassword />} />
      </Route>

      <Route
        path="/admin/*"
        element={
          <RequireAuth>
            <Suspense fallback={<FullPageLoading />}>
              <AdminApp />
            </Suspense>
          </RequireAuth>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
